import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { Sparkles, LogIn, Wand2, Download, Play, SkipBack, HelpCircle } from "lucide-react";
import { Toaster, toast } from "sonner";

import CompactVinyl from "./CompactVinyl";
import TrackInfoPanel from "./TrackInfoPanel";
import HorizontalWaveform from "./HorizontalWaveform";
import SyncBridge from "./SyncBridge";
import ControlBar from "./ControlBar";
import AddTrackPlaceholder from "./AddTrackPlaceholder";
import MyMixesSection from "./MyMixesSection";
import UserMixesSection from "./UserMixesSection";
import VolumeMeter from "./VolumeMeter";
import useWebAudioEngine from "../hooks/useWebAudioEngine";
import LoginButton from "./LoginButton";

const getBackendUrl = () => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname.includes("test.magicmixapp.ch")) return "https://test.magicmixapp.ch";
    if (hostname.includes("magicmixapp.ch")) return "https://magicmixapp.ch";
  }
  return process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || "https://test.magicmixapp.ch";
};
const API = `${getBackendUrl()}/api`;

const Home = ({ user }) => {
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [previewFile1, setPreviewFile1] = useState(null);
  const [previewFile2, setPreviewFile2] = useState(null);
  const [analysis1, setAnalysis1] = useState(null);
  const [analysis2, setAnalysis2] = useState(null);
  const [rotation1, setRotation1] = useState(0);
  const [rotation2, setRotation2] = useState(0);
  const [isPlaying1, setIsPlaying1] = useState(false);
  const [isPlaying2, setIsPlaying2] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [localSyncScore, setLocalSyncScore] = useState(null);
  const [isAligned, setIsAligned] = useState(false);
  const [crossfadeValue, setCrossfadeValue] = useState(50);
  const [bpm1Adjustment, setBpm1Adjustment] = useState(1.0);
  const [bpm2Adjustment, setBpm2Adjustment] = useState(1.0);
  const [gain1, setGain1] = useState(1.0);
  const [gain2, setGain2] = useState(1.0);
  const [recipes, setRecipes] = useState([]);
  const [audioReady1, setAudioReady1] = useState(false);
  const [audioReady2, setAudioReady2] = useState(false);

  const [showPaywall, setShowPaywall] = useState(false);
  const [exportsRemaining, setExportsRemaining] = useState(user?.exports_remaining || 0);

  // --- STATI PER LA NUOVA GUIDA ---
  const [showGuide, setShowGuide] = useState(false);
  const [guideMode, setGuideMode] = useState("onboarding"); // "onboarding" (corto) o "full" (lungo dal menu)

  // Nuovi stati per autenticazione posticipata strada B
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Controllo primo accesso automatico (Anonimi e Loggati)
  useEffect(() => {
    const guideKey = user ? `magicmix_guide_seen_${user.user_id}` : 'magicmix_guide_seen_anon';
    const hasSeenGuide = localStorage.getItem(guideKey) || localStorage.getItem('magicmix_guide_seen_anon');
    if (!hasSeenGuide) {
      setGuideMode("onboarding");
      setShowGuide(true);
    }
  }, [user]);

  // Ascolta se il menu esterno vuole aprire la guida (Quick Guide) o il Login dall'Header
  useEffect(() => {
    const handleOpenFullGuide = () => {
      setGuideMode("full");
      setShowGuide(true);
    };
    const handleOpenAuth = () => {
      setShowAuthModal(true);
    };
    window.addEventListener("magicmix_open_guide", handleOpenFullGuide);
    window.addEventListener("open_auth_modal", handleOpenAuth);
    return () => {
      window.removeEventListener("magicmix_open_guide", handleOpenFullGuide);
      window.removeEventListener("open_auth_modal", handleOpenAuth);
    };
  }, []);

  const closeGuideModal = () => {
    setShowGuide(false);
    if (guideMode === "onboarding") {
      const guideKey = user ? `magicmix_guide_seen_${user.user_id}` : 'magicmix_guide_seen_anon';
      localStorage.setItem(guideKey, "true");
    }
  };

  // Trigger automatico stateless del Paywall al rientro/completamento del Login
  useEffect(() => {
    if (user) {
      const isFreeOrExhausted = user.status !== 'Pro' && (user.exports_remaining === undefined || user.exports_remaining <= 0);
      if (isFreeOrExhausted) {
        setShowPaywall(true);
        setShowAuthModal(false); // Chiude il modal di login se era ancora aperto
      }
    }
  }, [user]);

  // --- INIZIO MOTORE ROTELLINA STRIPE (SOLO LOGICA) ---
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment') === 'success') {
      setIsVerifying(true);
      window.history.replaceState(null, '', window.location.pathname);
      setTimeout(() => {
        setIsVerifying(false);
      }, 4000);
    }
  }, []);
  // --- FINE MOTORE ROTELLINA STRIPE ---

  useEffect(() => {
    if (user && user.exports_remaining !== undefined) {
      setExportsRemaining(user.exports_remaining);
    }
  }, [user]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('magicmix_exports_updated', { detail: exportsRemaining }));
  }, [exportsRemaining]);

  const [readyToShare, setReadyToShare] = useState(null);
  
  const [stems1, setStems1] = useState(null);  
  const [stems2, setStems2] = useState(null);
  const [isSeparating1, setIsSeparating1] = useState(false);
  const [isSeparating2, setIsSeparating2] = useState(false);
  const [magicMixUrl, setMagicMixUrl] = useState(null);
  const [isMagicMixMode, setIsMagicMixMode] = useState(false);
  
  const [inMarker1, setInMarker1] = useState(0.1);   
  const [outMarker1, setOutMarker1] = useState(0.8); 
  const [inMarker2, setInMarker2] = useState(0.1);   
  const [outMarker2, setOutMarker2] = useState(0.9); 
  const [showMarkers, setShowMarkers] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState(null); 
  const crossfadeDuration = 3; 

  const [effectPowerStop, setEffectPowerStop] = useState(false);  
  const [effectBeatDrop, setEffectBeatDrop] = useState(false);    
  const [effectHighPass, setEffectHighPass] = useState(false);    

  const presets = {
    tiktok: { duration: 15, label: 'TikTok', icon: '📱' },
    reels: { duration: 30, label: 'Reels', icon: '🎬' },
    stories: { duration: 60, label: 'Stories', icon: '📸' }
  };

  const getSafeDuration = useCallback((analysis, audioRef) => {
    if (analysis && analysis.duration && !isNaN(analysis.duration)) return analysis.duration;
    if (audioRef && audioRef.current && audioRef.current.duration && !isNaN(audioRef.current.duration)) return audioRef.current.duration;
    return 180; 
  }, []);

  const trovaPuntoG = async (audioSource, mode) => {
    try {
      if (!audioSource) return null;
      let url = audioSource;
      if (typeof audioSource === 'object') {
          url = URL.createObjectURL(audioSource);
      }
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const channelData = audioBuffer.getChannelData(0);
      const sampleRate = audioBuffer.sampleRate;
      
      const totalSamples = channelData.length;
      const windowSize = sampleRate * 1; 
      
      let searchStart, searchEnd;
      if (mode === 'outro') {
        searchStart = Math.floor(totalSamples * 0.7);
        searchEnd = totalSamples - windowSize;
      } else {
        searchStart = 0;
        searchEnd = Math.floor(totalSamples * 0.3);
      }

      let bestPoint = 0;
      let minDiff = Infinity; 
      let maxDiff = 0;        

      for (let i = searchStart; i < searchEnd; i += windowSize / 2) {
        let energyCurrent = 0;
        let energyNext = 0;
        for (let j = 0; j < windowSize; j++) {
          energyCurrent += Math.abs(channelData[i + j] || 0);
          energyNext += Math.abs(channelData[i + j + windowSize] || 0);
        }
        const diff = energyNext - energyCurrent;
        if (mode === 'outro') {
          if (diff < minDiff) { minDiff = diff; bestPoint = i + windowSize; }
        } else {
          if (diff > maxDiff) { maxDiff = diff; bestPoint = i + windowSize; }
        }
      }
      return bestPoint / sampleRate;
    } catch (e) {
      return null;
    }
  };

  const applyPreset = useCallback(async (presetKey) => {
    if (!analysis1 || !analysis2) { 
      toast.error("Please load both tracks first!"); 
      return; 
    }
    const tid = toast.loading("Sniper at work: calculating perfect mix...", { id: 'cecchino' });
    const t1Punto = await trovaPuntoG(file1, 'outro');
    const t2Punto = await trovaPuntoG(file2, 'intro');
    const d1 = getSafeDuration(analysis1, audio1Ref);
    const d2 = getSafeDuration(analysis2, audio2Ref);
    const p1 = t1Punto ? (t1Punto / d1) : 0.85;
    const p2 = t2Punto ? (t2Punto / d2) : 0.10;
    
    const targetDur = presets[presetKey].duration;
    const needed = targetDur + crossfadeDuration;
    let t1C = Math.min(needed / 2, d1 * 0.8);
    
    const t1InPos = Math.max(0.02, p1 - (t1C / d1));
    const t2OutPos = Math.min(0.98, p2 + (t1C / d2)); 
    
    setInMarker1(t1InPos); 
    setOutMarker1(p1);
    setInMarker2(p2); 
    setOutMarker2(t2OutPos);
    setSelectedPreset(presetKey);
    toast.success("Sniper: Mix ready!", { id: tid });
  }, [analysis1, analysis2, crossfadeDuration, presets, file1, file2, getSafeDuration]);

  const handleMarkerChange = useCallback((setter, value) => {
    setter(value);
    if (selectedPreset) setSelectedPreset(null); 
  }, [selectedPreset]);

  const calculateMixDuration = useCallback(() => {
    if (!analysis1 || !analysis2) return 0;
    const t1Duration = (outMarker1 - inMarker1) * analysis1.duration;
    const t2Duration = (outMarker2 - inMarker2) * analysis2.duration;
    return Math.max(0, t1Duration + t2Duration - crossfadeDuration);
  }, [analysis1, analysis2, inMarker1, outMarker1, inMarker2, outMarker2, crossfadeDuration]);

  const mixDuration = calculateMixDuration();
  const webAudio = useWebAudioEngine();

  const audio1Ref = useRef(null);
  const audio2Ref = useRef(null);
  const previewTimeoutRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const audioUrl1Ref = useRef(null);
  const audioUrl2Ref = useRef(null);

  const audioCtxRef = useRef(null);
  const source1Ref = useRef(null);
  const source2Ref = useRef(null);

  const loadAudioBuffer = async (url) => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return await audioCtxRef.current.decodeAudioData(arrayBuffer);
  };

  useEffect(() => {
    const a1 = new Audio();
    const a2 = new Audio();
    a1.preload = 'auto';
    a2.preload = 'auto';
    a1.addEventListener('canplaythrough', () => setAudioReady1(true));
    a2.addEventListener('canplaythrough', () => setAudioReady2(true));
    a1.addEventListener('error', () => { setAudioReady1(false); setIsPlaying1(false); });
    a2.addEventListener('error', () => { setAudioReady2(false); setIsPlaying2(false); });
    audio1Ref.current = a1;
    audio2Ref.current = a2;
    
    return () => {
      a1.pause(); a2.pause();
      a1.src = ''; a2.src = '';
      if (audioUrl1Ref.current) URL.revokeObjectURL(audioUrl1Ref.current);
      if (audioUrl2Ref.current) URL.revokeObjectURL(audioUrl2Ref.current);
      if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (!previewFile1 || !audio1Ref.current) return;
    setAudioReady1(false);
    if (audioUrl1Ref.current) URL.revokeObjectURL(audioUrl1Ref.current);
    const newUrl = URL.createObjectURL(previewFile1);
    audioUrl1Ref.current = newUrl;
    audio1Ref.current.src = newUrl;
    audio1Ref.current.load();
  }, [previewFile1]);

  useEffect(() => {
    if (!previewFile2 || !audio2Ref.current) return;
    setAudioReady2(false);
    if (audioUrl2Ref.current) URL.revokeObjectURL(audioUrl2Ref.current);
    const newUrl = URL.createObjectURL(previewFile2);
    audioUrl2Ref.current = newUrl;
    audio2Ref.current.src = newUrl;
    audio2Ref.current.load();
  }, [previewFile2]);

  useEffect(() => {
    if (audio1Ref.current && audioReady1) audio1Ref.current.playbackRate = 1.0; 
  }, [bpm1Adjustment, audioReady1]);

  useEffect(() => {
    if (audio2Ref.current && audioReady2) audio2Ref.current.playbackRate = 1.0; 
  }, [bpm2Adjustment, audioReady2]);

  useEffect(() => {
    if (!audio1Ref.current || !analysis1 || !isPlaying1) return;
    const handleTimeUpdate = () => {
      if (analysis1.duration > 0) setRotation1((audio1Ref.current.currentTime / analysis1.duration) * 360);
    };
    audio1Ref.current.addEventListener('timeupdate', handleTimeUpdate);
    return () => audio1Ref.current?.removeEventListener('timeupdate', handleTimeUpdate);
  }, [analysis1, isPlaying1]);

  useEffect(() => {
    if (!audio2Ref.current || !analysis2 || !isPlaying2) return;
    const handleTimeUpdate = () => {
      if (analysis2.duration > 0) setRotation2((audio2Ref.current.currentTime / analysis2.duration) * 360);
    };
    audio2Ref.current.addEventListener('timeupdate', handleTimeUpdate);
    return () => audio2Ref.current?.removeEventListener('timeupdate', handleTimeUpdate);
  }, [analysis2, isPlaying2]);

  const checkAlignment = useCallback((rot1, rot2) => {
    if (!analysis1 || !analysis2) return;
    if (!analysis1.beat_timestamps?.length || !analysis2.beat_timestamps?.length) return;
    try {
      const time1 = (rot1 / 360) * analysis1.duration;
      const time2 = (rot2 / 360) * analysis2.duration;
      const nearestBeat1 = analysis1.beat_timestamps.reduce((prev, curr) => Math.abs(curr - time1) < Math.abs(prev - time1) ? curr : prev, analysis1.beat_timestamps[0]);
      const nearestBeat2 = analysis2.beat_timestamps.reduce((prev, curr) => Math.abs(curr - time2) < Math.abs(prev - time2) ? curr : prev, analysis2.beat_timestamps[0]);
      const avgOffset = (Math.abs(time1 - nearestBeat1) + Math.abs(time2 - nearestBeat2)) / 2;
      const syncPercent = Math.max(0, Math.min(100, (1 - avgOffset / 0.1) * 100));
      setLocalSyncScore(syncPercent);
      setIsAligned(syncPercent > 70);
    } catch (e) { }
  }, [analysis1, analysis2]);

  const handleRotation1Change = useCallback((newRot, isSnap = false) => {
    setRotation1(newRot);
    if (audio1Ref.current && audioReady1 && analysis1) {
      try {
        const newTime = (newRot / 360) * analysis1.duration;
        if (isFinite(newTime) && newTime >= 0 && newTime <= analysis1.duration) {
          audio1Ref.current.currentTime = newTime;
          if (!isPlaying1 && !isSnap) {
            audio1Ref.current.play().catch(() => {});
            setTimeout(() => { if (!isPlaying1 && audio1Ref.current) audio1Ref.current.pause(); }, 500);
          }
        }
      } catch (e) { }
    }
    checkAlignment(newRot, rotation2);
  }, [analysis1, audioReady1, isPlaying1, rotation2, checkAlignment]);

  const handleRotation2Change = useCallback((newRot, isSnap = false) => {
    setRotation2(newRot);
    if (audio2Ref.current && audioReady2 && analysis2) {
      try {
        const newTime = (newRot / 360) * analysis2.duration;
        if (isFinite(newTime) && newTime >= 0 && newTime <= analysis2.duration) {
          audio2Ref.current.currentTime = newTime;
          if (!isPlaying2 && !isSnap) {
            audio2Ref.current.play().catch(() => {});
            setTimeout(() => { if (!isPlaying2 && audio2Ref.current) audio2Ref.current.pause(); }, 500);
          }
        }
      } catch (e) { }
    }
    checkAlignment(rotation1, newRot);
  }, [analysis2, audioReady2, isPlaying2, rotation1, checkAlignment]);

  const handleSnapComplete = useCallback(() => {
    checkAlignment(rotation1, rotation2);
    if (isAligned) toast.success("Snapped to beat!", { icon: <Sparkles size={16} />, duration: 2000 });
  }, [rotation1, rotation2, isAligned, checkAlignment]);

  const requireLogin = () => {
    toast.error("Please sign in to use Magic Mix", { description: "Click 'Continue with Google' to get started", icon: <LogIn size={16} />, duration: 4000 });
  };

 const handleFile1Select = async (file) => {
    setFile1(file); setAnalysis1(null); setAudioReady1(false);
    
    try {
      toast.loading("Extracting audio...", { id: 'extract-1' });
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/extract-preview`, formData, { responseType: 'blob', withCredentials: true });
      setPreviewFile1(res.data);
      toast.success("Audio ready!", { id: 'extract-1' });
      try { await webAudio.loadTrack(res.data, 1); } catch (e) { }
    } catch (e) {
      toast.error("Audio extraction failed", { id: 'extract-1' });
    }
    
    await analyzeSingleTrack(file, setAnalysis1);
  };

  const handleFile2Select = async (file) => {
    setFile2(file); setAnalysis2(null); setAudioReady2(false);
    
    try {
      toast.loading("Extracting audio...", { id: 'extract-2' });
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/extract-preview`, formData, { responseType: 'blob', withCredentials: true });
      setPreviewFile2(res.data);
      toast.success("Audio ready!", { id: 'extract-2' });
      try { await webAudio.loadTrack(res.data, 2); } catch (e) { }
    } catch (e) {
      toast.error("Audio extraction failed", { id: 'extract-2' });
    }
    
    await analyzeSingleTrack(file, setAnalysis2);
  };

  const analyzeSingleTrack = async (file, setAnalysis) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      toast.loading("Analyzing track...", { id: 'analysis' });
      const response = await axios.post(`${API}/analyze/single`, formData, { 
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true 
      });
      setAnalysis(response.data);
      toast.success(`${response.data.bpm} BPM • ${response.data.beat_count} beats`, { id: 'analysis' });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Analysis failed";
      
      if (status === 403 || status === 429) {
        toast.error("Upload Limit Reached 🛑", { description: detail, id: 'analysis', duration: 8000 });
      } else {
        toast.error(detail, { id: 'analysis' });
      }
    }
  };

  const togglePlayback = () => {
    if (!audioReady1 || !audioReady2) { toast.error("Wait for tracks to load completely"); return; }
    if (isPreviewing && audioCtxRef.current) {
      if (audioCtxRef.current.state === 'running') {
        audioCtxRef.current.suspend(); setIsPlaying1(false); setIsPlaying2(false);
      } else if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume(); setIsPlaying1(true); setIsPlaying2(true);
      }
      return;
    }
    handlePreview();
  };

  const toggleTrack1 = () => {
    if (!audio1Ref.current || !audioReady1) return;
    if (isPlaying1) { audio1Ref.current.pause(); setIsPlaying1(false); }
    else { audio1Ref.current.playbackRate = 1.0; audio1Ref.current.play().catch(()=>{}); setIsPlaying1(true); }
  };

  const restartTrack1 = () => {
    if (!audio1Ref.current || !audioReady1) return;
    setRotation1(0); audio1Ref.current.currentTime = 0;
    if (isPlaying1) audio1Ref.current.play().catch(()=>{});
  };

  const toggleTrack2 = () => {
    if (!audio2Ref.current || !audioReady2) return;
    if (isPlaying2) { audio2Ref.current.pause(); setIsPlaying2(false); }
    else { audio2Ref.current.playbackRate = 1.0; audio2Ref.current.play().catch(()=>{}); setIsPlaying2(true); }
  };

  const restartTrack2 = () => {
    if (!audio2Ref.current || !audioReady2) return;
    setRotation2(0); audio2Ref.current.currentTime = 0;
    if (isPlaying2) audio2Ref.current.play().catch(()=>{});
  };

  const stopPreview = useCallback(() => {
    setIsPreviewing(false); setIsPlaying1(false); setIsPlaying2(false);
    if (audio1Ref.current) { audio1Ref.current.pause(); audio1Ref.current.currentTime = 0; }
    if (audio2Ref.current) { audio2Ref.current.pause(); audio2Ref.current.currentTime = 0; }
    if (source1Ref.current) { try { source1Ref.current.stop(); } catch(e){} source1Ref.current.disconnect(); source1Ref.current = null; }
    if (source2Ref.current) { try { source2Ref.current.stop(); } catch(e){} source2Ref.current.disconnect(); source2Ref.current = null; }
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
  }, []);

  const handleFile1Clear = useCallback(() => {
    stopPreview(); webAudio.clearTrack(1);
    if (audio1Ref.current?.src) URL.revokeObjectURL(audio1Ref.current.src);
    if (audio1Ref.current) { try { audio1Ref.current.pause(); audio1Ref.current.currentTime = 0; audio1Ref.current.src = ''; audio1Ref.current.load(); } catch (e) { } }
    setFile1(null); setPreviewFile1(null); setAnalysis1(null); setAudioReady1(false); setRotation1(0);
    setIsPlaying1(false); setBpm1Adjustment(1.0); setGain1(1.0); setInMarker1(0.1); setOutMarker1(0.8);
  }, [stopPreview, webAudio]);

  const handleFile2Clear = useCallback(() => {
    stopPreview(); webAudio.clearTrack(2);
    if (audio2Ref.current?.src) URL.revokeObjectURL(audio2Ref.current.src);
    if (audio2Ref.current) { try { audio2Ref.current.pause(); audio2Ref.current.currentTime = 0; audio2Ref.current.src = ''; audio2Ref.current.load(); } catch (e) { } }
    setFile2(null); setPreviewFile2(null); setAnalysis2(null); setAudioReady2(false); setRotation2(0);
    setIsPlaying2(false); setBpm2Adjustment(1.0); setGain2(1.0); setInMarker2(0.1); setOutMarker2(0.9);
  }, [stopPreview, webAudio]);

  const handleSwapTracks = useCallback(() => {
    stopPreview();
    if (audio1Ref.current) audio1Ref.current.pause();
    if (audio2Ref.current) audio2Ref.current.pause();
    setFile1(file2); setFile2(file1);
    setPreviewFile1(previewFile2); setPreviewFile2(previewFile1);
    setAnalysis1(analysis2); setAnalysis2(analysis1);
    setAudioReady1(audioReady2); setAudioReady2(audioReady1);
    setInMarker1(inMarker2); setOutMarker1(outMarker2);
    setInMarker2(inMarker1); setOutMarker2(outMarker1);
    setGain1(gain2); setGain2(gain1);
    setBpm1Adjustment(bpm2Adjustment); setBpm2Adjustment(bpm1Adjustment);
    toast.success("Decks Swapped", { icon: "🔄", duration: 2000 });
  }, [file1, file2, analysis1, analysis2, audioReady1, audioReady2, inMarker1, outMarker1, inMarker2, outMarker2, gain1, gain2, bpm1Adjustment, bpm2Adjustment, stopPreview]);

  const handleMagneticSnap = async () => {
    if (!audio1Ref.current?.src || !audio2Ref.current?.src || !analysis1 || !analysis2) { toast.error("Load and analyze both tracks first!"); return; }
    toast.loading("Magnet in action...", { id: "magnet" });
    try {
      const { guess } = await import("web-audio-beat-detector");
      const ctx1 = new (window.AudioContext || window.webkitAudioContext)();
      const buf1 = await ctx1.decodeAudioData(await (await fetch(audio1Ref.current.src)).arrayBuffer());
      const res1 = await guess(buf1);
      const ctx2 = new (window.AudioContext || window.webkitAudioContext)();
      const buf2 = await ctx2.decodeAudioData(await (await fetch(audio2Ref.current.src)).arrayBuffer());
      const res2 = await guess(buf2);

      const currentT1Out = outMarker1 * analysis1.duration;
      const currentT2In = inMarker2 * analysis2.duration;

      const beatLen1 = 60 / res1.bpm;
      const snappedT1Out = res1.offset + (Math.round((currentT1Out - res1.offset) / beatLen1) * beatLen1);

      const beatLen2 = 60 / res2.bpm;
      const snappedT2In = res2.offset + (Math.round((currentT2In - res2.offset) / beatLen2) * beatLen2);

      setOutMarker1(snappedT1Out / analysis1.duration);
      setInMarker2(snappedT2In / analysis2.duration);

      toast.success(`Mix Aligned! (T1: ${Math.round(res1.bpm)} BPM, T2: ${Math.round(res2.bpm)} BPM)`, { id: "magnet" });
    } catch (error) { toast.error("Error during alignment.", { id: "magnet" }); }
  };

  const handlePreview = useCallback(async () => {
    if (!audioReady1 || !audioReady2 || !analysis1 || !analysis2) { toast.error("Wait for tracks to load completely"); return; }
    stopPreview(); setIsPreviewing(true); toast.loading("Generating Preview...", { id: 'preview-run' });

    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtxRef.current.state === 'suspended') await audioCtxRef.current.resume();
      const ctx = audioCtxRef.current;

      const buffer1 = await loadAudioBuffer(audio1Ref.current.src);
      const buffer2 = await loadAudioBuffer(audio2Ref.current.src);

      const t1InTime = Number(inMarker1 * (analysis1.duration || 1)) || 0;
      const t2InTime = Number(inMarker2 * (analysis2.duration || 1)) || 0;
      const t1OutTime = Number(outMarker1 * (analysis1.duration || 1)) || (t1InTime + 10);
      let visualDuration = t1OutTime - t1InTime; if (visualDuration <= 2) visualDuration = 10; 
      
      const safeGain1 = isFinite(gain1) ? Number(gain1) : 1.0;
      const safeGain2 = isFinite(gain2) ? Number(gain2) : 1.0;
      const safeCrossfade = isFinite(crossfadeDuration) ? Number(crossfadeDuration) : 4.0;
      const powerStopDuration = 1.4; 
      
      let preCrossfadeDuration = effectPowerStop ? (visualDuration - powerStopDuration) : (visualDuration - safeCrossfade);
      if (preCrossfadeDuration < 0.1) preCrossfadeDuration = 0.1; 
      
      const source1 = ctx.createBufferSource(); const source2 = ctx.createBufferSource();
      const gainNode1 = ctx.createGain(); const gainNode2 = ctx.createGain();
      source1.buffer = buffer1; source2.buffer = buffer2; source2.playbackRate.value = 1.0; 

      source1.connect(gainNode1).connect(ctx.destination);
      source2.connect(gainNode2).connect(ctx.destination);
      source1Ref.current = source1; source2Ref.current = source2;

      const startTime = ctx.currentTime + 0.1; 
      if (audio1Ref.current) audio1Ref.current.currentTime = t1InTime;
      if (audio2Ref.current) audio2Ref.current.currentTime = t2InTime;

      source1.start(startTime, t1InTime); setIsPlaying1(true);
      toast.success("Playing Mix!", { id: 'preview-run', duration: 1500 });

      if (effectPowerStop) {
        gainNode1.gain.setValueAtTime(safeGain1, startTime);
        const brakeStart = startTime + preCrossfadeDuration;
        source1.playbackRate.setValueAtTime(1.0, brakeStart);
        source1.playbackRate.exponentialRampToValueAtTime(0.1, brakeStart + powerStopDuration);
        gainNode1.gain.setValueAtTime(safeGain1, brakeStart);
        gainNode1.gain.exponentialRampToValueAtTime(0.05, brakeStart + powerStopDuration - 0.1);
        gainNode1.gain.linearRampToValueAtTime(0.0001, brakeStart + powerStopDuration);
        gainNode1.gain.setValueAtTime(0.0001, brakeStart + powerStopDuration); 
        source1.stop(brakeStart + powerStopDuration);
        setTimeout(() => setIsPlaying1(false), (preCrossfadeDuration + powerStopDuration) * 1000);
      } else {
        gainNode1.gain.setValueAtTime(safeGain1, startTime);
        gainNode1.gain.setValueAtTime(safeGain1, startTime + preCrossfadeDuration);
        gainNode1.gain.linearRampToValueAtTime(0.001, startTime + preCrossfadeDuration + safeCrossfade);
        source1.stop(startTime + preCrossfadeDuration + safeCrossfade);
        setTimeout(() => setIsPlaying1(false), (preCrossfadeDuration + safeCrossfade) * 1000);
      }

      const t2StartTime = effectPowerStop ? (startTime + preCrossfadeDuration + powerStopDuration) : (startTime + preCrossfadeDuration);

      if (effectBeatDrop) {
          if (effectPowerStop) {
            gainNode2.gain.setValueAtTime(0.001, t2StartTime);
            gainNode2.gain.linearRampToValueAtTime(safeGain2 * 1.5, t2StartTime + 0.05);
          } else { gainNode2.gain.setValueAtTime(safeGain2 * 1.5, t2StartTime); }
        gainNode2.gain.exponentialRampToValueAtTime(safeGain2, t2StartTime + 1.0);
      } else {
        gainNode2.gain.cancelScheduledValues(0);
        if (!effectPowerStop) {
          gainNode2.gain.setValueAtTime(0.001, t2StartTime);
          gainNode2.gain.linearRampToValueAtTime(safeGain2, t2StartTime + safeCrossfade);
        } else {
          gainNode2.gain.setValueAtTime(0.005, t2StartTime);
          gainNode2.gain.exponentialRampToValueAtTime(safeGain2, t2StartTime + 2.5);
        }
      }

      const ritardo_ps = effectPowerStop ? 0.2 : 0;
      source2.start(t2StartTime + ritardo_ps, t2InTime);
      setTimeout(() => setIsPlaying2(true), (t2StartTime + ritardo_ps - startTime + 0.1) * 1000);

      const t2OutTime = Number(outMarker2 * (analysis2.duration || 1)) || (t2InTime + 15);
      let t2PlayDuration = t2OutTime - t2InTime; if (t2PlayDuration <= 2) t2PlayDuration = 10; 

      source2.stop(t2StartTime + ritardo_ps + t2PlayDuration);
      previewTimeoutRef.current = setTimeout(() => stopPreview(), ((t2StartTime + ritardo_ps + t2PlayDuration) - startTime + 0.1) * 1000);

    } catch (err) { toast.error("Live mix failed."); stopPreview(); }
  }, [analysis1, analysis2, audioReady1, audioReady2, stopPreview, inMarker1, outMarker1, inMarker2, outMarker2, crossfadeDuration, gain1, gain2, effectPowerStop, effectBeatDrop]);

  useEffect(() => {
    let animationFrameId; let lastTime = performance.now();
    const updateVisuals = (currentTime) => {
      if (!isPreviewing || !audioCtxRef.current || audioCtxRef.current.state !== 'running') return;
      const delta = (currentTime - lastTime) / 1000; lastTime = currentTime;
      if (audio1Ref.current && isPlaying1) audio1Ref.current.currentTime += delta;
      if (audio2Ref.current && isPlaying2) audio2Ref.current.currentTime += delta;
      animationFrameId = requestAnimationFrame(updateVisuals);
    };
    if (isPreviewing) { lastTime = performance.now(); animationFrameId = requestAnimationFrame(updateVisuals); }
    return () => cancelAnimationFrame(animationFrameId);
  }, [isPreviewing, isPlaying1, isPlaying2]);

  const executeShare = async () => {
    if (!readyToShare) return;
    
    const forceDownload = () => {
      const a = document.createElement("a");
      a.href = readyToShare.url;
      a.download = readyToShare.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Video saved to Downloads!", { id: 'share-exec' });
      setReadyToShare(null);
    };

    if (navigator.canShare && navigator.canShare({ files: [readyToShare.file] })) {
      try {
        await navigator.share({
          files: [readyToShare.file]
        });
        toast.success("Shared successfully!", { id: 'share-exec' });
        setReadyToShare(null); 
      } catch (err) {
        forceDownload();
      }
    } else {
      forceDownload();
    }
  };

  const handleSocialExport = async (platform) => {
    if (!file1 || !file2 || !analysis1 || !analysis2) { toast.error("Please upload and analyze both tracks first"); return; }
    if (!user) { setShowAuthModal(true); setShowExportMenu(false); return; }
    if (exportsRemaining <= 0) { setShowPaywall(true); setShowExportMenu(false); return; }

    setIsExporting(true);

    const t1InTime = inMarker1 * analysis1.duration;
    const t1OutTime = outMarker1 * analysis1.duration;
    const t2InTime = inMarker2 * analysis2.duration;
    const t2OutTime = outMarker2 * analysis2.duration;
    const totalDuration = (t1OutTime - t1InTime) + (t2OutTime - t2InTime) - crossfadeDuration;

    toast.loading(`Mixing video for ${platform}...`, { id: 'export-social' });

    const formData = new FormData();
    formData.append('file1', file1); formData.append('file2', file2);
    formData.append('track1_in', String(t1InTime.toFixed(3))); formData.append('track1_out', String(t1OutTime.toFixed(3)));
    formData.append('track2_in', String(t2InTime.toFixed(3))); formData.append('track2_out', String(t2OutTime.toFixed(3)));
    formData.append('crossfade_duration', String(crossfadeDuration));
    formData.append('track1_bpm', String(analysis1.bpm || 120)); formData.append('track2_bpm', String(analysis2.bpm || 120));
    formData.append('target_bpm', String(((analysis1.bpm || 120) + (analysis2.bpm || 120)) / 2));
    formData.append('track1_gain', String(gain1)); formData.append('track2_gain', String(gain2));
    formData.append('use_dynamic_ramp', 'true'); formData.append('flexible_duration', 'true');
    formData.append('effect_power_stop', String(effectPowerStop)); formData.append('effect_beat_drop', String(effectBeatDrop));
    formData.append('effect_high_pass', String(effectHighPass)); formData.append('format_type', 'social');

    try {
      const response = await fetch(`${API}/export/medley`, { method: 'POST', body: formData });
      
      if (!response.ok) {
        let errorMsg = "Video export failed";
        try {
            const errorData = await response.json();
            errorMsg = errorData.error || errorData.detail || errorMsg;
        } catch(e) { }
        
        if (response.status === 403) {
            setShowPaywall(true);
            toast.dismiss('export-social');
            return;
        }
        throw new Error(errorMsg);
      }

      setExportsRemaining(prev => prev - 1);
      
      const blob = await response.blob();
      const file = new File([blob], `magicmix_${platform}.mp4`, { type: 'video/mp4' });
      const url = window.URL.createObjectURL(blob);
      const filename = `magicmix_${platform}_${Math.round(totalDuration)}s.mp4`;

      setReadyToShare({ file, platform, url, filename });
      toast.dismiss('export-social');

    } catch (error) {
      toast.error(error.message || String(error), { id: 'export-social', duration: 8000 });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExport = async () => {
    if (!file1 || !file2 || !analysis1 || !analysis2) { toast.error("Please upload and analyze both tracks first"); return; }
    if (!user) { setShowAuthModal(true); setShowExportMenu(false); return; }
    if (exportsRemaining <= 0) { setShowPaywall(true); setShowExportMenu(false); return; }

    setIsExporting(true);
    const t1InTime = inMarker1 * analysis1.duration; const t1OutTime = outMarker1 * analysis1.duration;
    const t2InTime = inMarker2 * analysis2.duration; const t2OutTime = outMarker2 * analysis2.duration;
    const totalDuration = (t1OutTime - t1InTime) + (t2OutTime - t2InTime) - crossfadeDuration;

    toast.loading(`Creating ${totalDuration.toFixed(1)}s mix...`, { id: 'export' });

    const formData = new FormData();
    formData.append('file1', file1); formData.append('file2', file2);
    formData.append('track1_in', String(t1InTime.toFixed(3))); formData.append('track1_out', String(t1OutTime.toFixed(3)));
    formData.append('track2_in', String(t2InTime.toFixed(3))); formData.append('track2_out', String(t2OutTime.toFixed(3)));
    formData.append('crossfade_duration', String(crossfadeDuration));
    formData.append('track1_bpm', String(analysis1.bpm || 120)); formData.append('track2_bpm', String(analysis2.bpm || 120));
    formData.append('target_bpm', String(((analysis1.bpm || 120) + (analysis2.bpm || 120)) / 2));
    formData.append('track1_gain', String(gain1)); formData.append('track2_gain', String(gain2));
    formData.append('use_dynamic_ramp', 'true'); formData.append('flexible_duration', 'true');
    formData.append('effect_power_stop', String(effectPowerStop)); formData.append('effect_beat_drop', String(effectBeatDrop));
    formData.append('effect_high_pass', String(effectHighPass));

    try {
      const response = await fetch(`${API}/export/medley`, { method: 'POST', body: formData });
      
      if (!response.ok) {
        let errorMsg = "Export failed";
        try {
            const errorData = await response.json();
            errorMsg = errorData.error || errorData.detail || errorMsg;
        } catch(e) { }
        
        if (response.status === 403) {
            setShowPaywall(true);
            toast.dismiss('export');
            return;
        }
        throw new Error(errorMsg);
      }
      
      setExportsRemaining(prev => prev - 1);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `magicmix_mix_${Math.round(totalDuration)}s_${Date.now()}.mp3`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 100);

      toast.success(`${totalDuration.toFixed(1)}s mix exported!`, { id: 'export', icon: <Download size={16} /> });
    } catch (err) { 
      toast.error(err.message || "Export failed.", { id: 'export', duration: 8000 }); 
    } finally { 
      setIsExporting(false); 
    }
  };

  if (isVerifying) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#000', color: '#fff' }}>
        <div style={{ width: '50px', height: '50px', border: '5px solid rgba(0, 242, 254, 0.3)', borderTopColor: '#00f2fe', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        <h2 style={{ marginTop: '20px', fontFamily: 'sans-serif' }}>Verifying payment...</h2>
        <p style={{ color: '#888', fontFamily: 'sans-serif' }}>Setting up your magic. Please wait.</p>
      </div>
    );
  }

  return (
    <div className="mobile-app" data-testid="mobile-app">
      <header className="mobile-header">
  <h1 style={{ color: '#00f0ff', fontSize: '28px', fontWeight: '900', letterSpacing: '3px', margin: 0, textTransform: 'uppercase' }}>MAGIC MIX</h1>
</header>

      <section className="deck deck-1" data-testid="deck-1">
        <div className="deck-content">
          <CompactVinyl file={file1} onFileSelect={handleFile1Select} onFileClear={handleFile1Clear} trackNumber={1} analysis={analysis1} rotation={rotation1} onRotationChange={handleRotation1Change} onSnapComplete={handleSnapComplete} isPlaying={isPlaying1} bpmAdjustment={bpm1Adjustment} isLoggedIn={!!user} onRequireLogin={requireLogin} />
          <TrackInfoPanel analysis={analysis1} rotation={rotation1} bpmAdjustment={bpm1Adjustment} trackNumber={1} isPlaying={isPlaying1} onPlayPause={toggleTrack1} onRestart={restartTrack1} />
        </div>
        <HorizontalWaveform beats={analysis1?.beat_timestamps} duration={analysis1?.duration} rotation={rotation1} onSeek={handleRotation1Change} trackNumber={1} isActive={audioReady1} color="purple" inMarker={inMarker1} outMarker={outMarker1} onInMarkerChange={(v) => handleMarkerChange(setInMarker1, v)} onOutMarkerChange={(v) => handleMarkerChange(setOutMarker1, v)} showMarkers={showMarkers && !!analysis1} />
        {audioReady1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '10px', marginBottom: '10px' }}>
            <button onClick={() => setInMarker1(rotation1 / 360)} style={{ background: 'transparent', color: '#FF4E4E', padding: '10px 25px', borderRadius: '30px', border: '2px solid #FF4E4E', fontWeight: '900', fontSize: '12px', cursor: 'pointer', boxShadow: '0 0 10px rgba(255, 78, 78, 0.4)' }}>📍 SET IN</button>
            <button onClick={() => setOutMarker1(rotation1 / 360)} style={{ background: 'transparent', color: '#00F2FF', padding: '10px 25px', borderRadius: '30px', border: '2px solid #00F2FF', fontWeight: '900', fontSize: '12px', cursor: 'pointer', boxShadow: '0 0 10px rgba(0, 242, 255, 0.4)' }}>🎯 SET OUT</button>
          </div>
        )}
        {file1 && !audioReady1 && <div className="loading-indicator">Loading audio...</div>}
      </section>

      <div className="sync-area sandwich-layout" data-testid="sync-area" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'stretch', gap: '8px', padding: '15px 10px', width: '100%', minHeight: '200px', background: '#09090b', borderTop: '2px solid #27272a', borderBottom: '2px solid #27272a', margin: '10px 0', boxShadow: 'inset 0 0 20px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '8px', flex: '1.1' }}>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', height: '50px' }}>
            <button onClick={togglePlayback} style={{ background: 'rgba(39, 39, 42, 0.8)', color: 'white', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)', fontWeight: 'bold', fontSize: '20px', cursor: 'pointer', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.5)' }}>{(isPreviewing && (isPlaying1 || isPlaying2)) ? "⏸" : "▶"}</button>
            <button onClick={() => toast.success("Cut applied!")} style={{ background: 'rgba(39, 39, 42, 0.8)', color: 'white', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)', fontWeight: 'bold', fontSize: '20px', cursor: 'pointer', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.5)' }}>✂️</button>
          </div>
          <button onClick={handlePreview} style={{ background: '#7c3aed', color: 'white', padding: '12px 5px', borderRadius: '25px', border: 'none', fontWeight: '900', fontSize: '11px', cursor: 'pointer', flex: 1, boxShadow: '0 0 15px rgba(124, 58, 237, 0.4)' }}>👁️ PREVIEW</button>
          {showExportMenu ? (
             <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', flex: 1 }}>
                <button disabled={isExporting} onClick={() => { handleSocialExport('tiktok'); setShowExportMenu(false); }} style={{ background: '#18181b', color: '#00f2fe', borderRadius: '8px', border: '1px solid #00f2fe', fontWeight: 'bold', fontSize: '11px', cursor: isExporting ? 'not-allowed' : 'pointer', opacity: isExporting ? 0.5 : 1 }}>TT</button>
                <button disabled={isExporting} onClick={() => { handleSocialExport('instagram'); setShowExportMenu(false); }} style={{ background: '#18181b', color: '#fe0979', borderRadius: '8px', border: '1px solid #fe0979', fontWeight: 'bold', fontSize: '11px', cursor: isExporting ? 'not-allowed' : 'pointer', opacity: isExporting ? 0.5 : 1 }}>IG</button>
                <button disabled={isExporting} onClick={() => { handleExport(); setShowExportMenu(false); }} style={{ background: '#18181b', color: '#10b981', borderRadius: '8px', border: '1px solid #10b981', fontWeight: 'bold', fontSize: '11px', cursor: isExporting ? 'not-allowed' : 'pointer', opacity: isExporting ? 0.5 : 1 }}>MP3</button>
                <button onClick={() => setShowExportMenu(false)} style={{ background: '#27272a', color: 'white', borderRadius: '8px', border: 'none', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer' }}>✕</button>
             </div>
          ) : (
            <button disabled={isExporting} onClick={() => setShowExportMenu(true)} style={{ background: 'linear-gradient(45deg, #06b6d4, #ec4899)', color: 'white', padding: '12px 5px', borderRadius: '25px', border: 'none', fontWeight: '900', fontSize: '10px', cursor: isExporting ? 'not-allowed' : 'pointer', opacity: isExporting ? 0.5 : 1, flex: 1, boxShadow: '0 4px 15px rgba(236, 72, 153, 0.3)' }}>{isExporting ? '⏳ DOWNLOADING...' : '🚀 DOWNLOAD'}</button>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flex: '0.9', borderLeft: '1px solid #27272a', borderRight: '1px solid #27272a' }}>
          <button onClick={handleSwapTracks} style={{ background: '#18181b', border: '2px solid #3b82f6', borderRadius: '50%', width: '55px', height: '55px', fontSize: '22px', cursor: 'pointer', boxShadow: '0 0 15px rgba(59, 130, 246, 0.3)' }}>🔄</button>
          <div style={{ transform: 'scale(0.8)' }}><SyncBridge syncScore={localSyncScore} isAligned={isAligned} /></div>
          {analysis1 && analysis2 && (
            <div style={{ textAlign: 'center', background: 'rgba(0,0,0,0.6)', padding: '6px 10px', borderRadius: '20px', border: '1px solid #27272a' }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#10b981', fontFamily: 'monospace' }}>{mixDuration.toFixed(1)}s</div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '6px', flex: '1.1' }}>
          <button onClick={handleMagneticSnap} style={{ background: '#27272a', color: '#3b82f6', borderRadius: '12px', border: '1px solid #3b82f6', flex: 0.8, cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>🧲 SNAP</button>
          {[ {id:'tiktok', label:'15s', icon:'📱', eff:effectPowerStop, setEff:setEffectPowerStop, eIcon:'📉', eCol:'#eab308'},
             {id:'reels', label:'30s', icon:'🎬', eff:effectBeatDrop, setEff:setEffectBeatDrop, eIcon:'💥', eCol:'#ec4899'},
             {id:'stories', label:'60s', icon:'📸', eff:effectHighPass, setEff:setEffectHighPass, eIcon:'🤖', eCol:'#3b82f6'}
          ].map(p => (
            <div key={p.id} style={{ display: 'flex', gap: '4px', flex: 1 }}>
              <button onClick={() => applyPreset(p.id)} style={{ background: selectedPreset === p.id ? 'rgba(255,255,255,0.1)' : '#18181b', color: 'white', borderRadius: '10px', border: selectedPreset === p.id ? '1px solid #fff' : '1px solid #27272a', flex: 1, fontSize: '10px', fontWeight: 'bold' }}>{p.label} {p.icon}</button>
              <button onClick={() => p.setEff(!p.eff)} style={{ background: p.eff ? p.eCol : '#27272a', color: p.eff ? '#000' : '#fff', borderRadius: '10px', border: 'none', flex: 1, fontSize: '14px', transition: '0.3s', boxShadow: p.eff ? `0 0 10px ${p.eCol}66` : 'none' }}>{p.eIcon}</button>
            </div>
          ))}
        </div>
      </div>

      <section className="deck deck-2" data-testid="deck-2">
        <div className="deck-content">
          <CompactVinyl file={file2} onFileSelect={handleFile2Select} onFileClear={handleFile2Clear} trackNumber={2} analysis={analysis2} rotation={rotation2} onRotationChange={handleRotation2Change} onSnapComplete={handleSnapComplete} isPlaying={isPlaying2} bpmAdjustment={bpm2Adjustment} isLoggedIn={!!user} onRequireLogin={requireLogin} />
          <TrackInfoPanel analysis={analysis2} rotation={rotation2} bpmAdjustment={bpm2Adjustment} trackNumber={2} isPlaying={isPlaying2} onPlayPause={toggleTrack2} onRestart={restartTrack2} />
        </div>
        <HorizontalWaveform beats={analysis2?.beat_timestamps} duration={analysis2?.duration} rotation={rotation2} onSeek={handleRotation2Change} trackNumber={2} isActive={audioReady2} color="green" inMarker={inMarker2} outMarker={outMarker2} onInMarkerChange={(v) => handleMarkerChange(setInMarker2, v)} onOutMarkerChange={(v) => handleMarkerChange(setOutMarker2, v)} showMarkers={showMarkers && !!analysis2} />
        {audioReady2 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '10px', marginBottom: '10px' }}>
            <button onClick={() => setInMarker2(rotation2 / 360)} style={{ background: 'transparent', color: '#FF4E4E', padding: '10px 25px', borderRadius: '30px', border: '2px solid #FF4E4E', fontWeight: '900', fontSize: '12px', cursor: 'pointer', boxShadow: '0 0 10px rgba(255, 78, 78, 0.4)' }}>📍 SET IN</button>
            <button onClick={() => setOutMarker2(rotation2 / 360)} style={{ background: 'transparent', color: '#00F2FF', padding: '10px 25px', borderRadius: '30px', border: '2px solid #00F2FF', fontWeight: '900', fontSize: '12px', cursor: 'pointer', boxShadow: '0 0 10px rgba(0, 242, 255, 0.4)' }}>🎯 SET OUT</button>
          </div>
        )}
        {file2 && !audioReady2 && <div className="loading-indicator">Loading audio...</div>}
      </section>

      {/* 🚀 DYNAMIC COMPREHENSIVE USER GUIDE & ONBOARDING MODAL */}
      {showGuide && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 99999999, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(8px)', fontFamily: 'sans-serif' }}>
          <div style={{ backgroundColor: '#111', border: '2px solid #a370f7', borderRadius: '16px', padding: '25px', width: '90%', maxWidth: '450px', maxHeight: '80vh', overflowY: 'auto', textAlign: 'left', color: '#c5c6c7', boxShadow: '0 0 30px rgba(163, 112, 247, 0.3)' }}>
            
            {guideMode === "onboarding" ? (
              /* --- VIEW 1: ONBOARDING CORTO AL PRIMO LOGIN --- */
              <>
                <h2 style={{ color: '#00f0ff', fontSize: '24px', fontWeight: '900', textAlign: 'center', marginTop: 0, letterSpacing: '1px' }}>WELCOME TO MAGIC MIX 🎵</h2>
                <p style={{ fontSize: '14px', lineHeight: '1.5', color: '#fff', textAlign: 'center' }}>Blend two tracks into one unique sound with a smooth Power Stop transition.</p>
                <div style={{ borderBottom: '1px solid #27272a', margin: '15px 0' }}></div>
                
                <h3 style={{ color: '#a370f7', fontSize: '16px', marginBottom: '10px' }}>How it works in 4 steps:</h3>
                
                <div style={{ spaceY: '12px', fontSize: '14px', lineHeight: '1.4' }}>
                  <p><strong>1. Upload your tracks</strong><br /><span style={{ color: '#aaa' }}>Add up to two sounds. You can upload an audio file or a video — Magic Mix automatically extracts the audio from videos for you.</span></p>
                  <p><strong>2. Set your transition</strong><br /><span style={{ color: '#aaa' }}>Tap the smart timers (<strong>15s, 30s, 60s</strong>) to let the app instantly calculate the perfect drop. Want total control? Use <strong>📍 SET IN</strong> and <strong>🎯 SET OUT</strong> to adjust markers manually.</span></p>
                  <p><strong>3. Preview</strong><br /><span style={{ color: '#aaa' }}>Listen to your mix before saving. Adjust the transition until it feels right.</span></p>
                  <p><strong>4. Download</strong><br /><span style={{ color: '#aaa' }}>Download your mix as an audio file (MP3) or as a social-ready video file (MP4).</span></p>
                </div>
                
                <div style={{ borderBottom: '1px solid #27272a', margin: '15px 0' }}></div>
                <p style={{ fontSize: '12px', color: '#aaa', display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                  <span>🔒</span> <span><strong>Your privacy:</strong> Your privacy is important, your data is safe under your profile. We only use your login to save your creations.</span>
                </p>
              </>
            ) : (
              /* --- VIEW 2: HELP CENTER COMPLETO CON LEGENDA --- */
              <>
                <h2 style={{ color: '#00f0ff', fontSize: '24px', fontWeight: '900', textAlign: 'center', marginTop: 0, letterSpacing: '1px' }}>MAGIC MIX — USER GUIDE</h2>
                
                <h3 style={{ color: '#a370f7', fontSize: '16px', marginTop: '15px', marginBottom: '5px' }}>Quick Start</h3>
                <ol style={{ fontSize: '13px', color: '#aaa', paddingLeft: '20px', margin: '5px 0', lineHeight: '1.4' }}>
                  <li><strong>Upload:</strong> Files or videos up to 25 MB.</li>
                  <li><strong>Transition:</strong> Use smart timers or manual buttons.</li>
                  <li><strong>Preview & Export:</strong> Listen live, then download your MP3/MP4.</li>
                </ol>
                
                <div style={{ borderBottom: '1px solid #27272a', margin: '15px 0' }}></div>
                <h3 style={{ color: '#a370f7', fontSize: '16px', marginBottom: '10px' }}>Control Legend</h3>
                
                <h4 style={{ color: '#fff', fontSize: '14px', margin: '10px 0 5px 0' }}>Interface Buttons</h4>
                <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0, fontSize: '13px', lineHeight: '1.5', color: '#aaa' }}>
                  <li style={{ marginBottom: '6px' }}>👀 <strong>PREVIEW</strong> — Listen to your live mix.</li>
                  <li style={{ marginBottom: '6px' }}>🚀 <strong>DOWNLOAD</strong> — Download your final mix.</li>
                  <li style={{ marginBottom: '6px' }}>✂️ <strong>Trim (Scissors)</strong> — Cut your track.</li>
                  <li style={{ marginBottom: '6px' }}>🔄 <strong>Sync (Arrows)</strong> — Synchronize the two tracks.</li>
                  <li style={{ marginBottom: '6px' }}>🎧 <strong>SNAP</strong> — Align your cuts perfectly to the beat.</li>
                  <li style={{ marginBottom: '6px' }}>⏱️ <strong>15s / 30s / 60s</strong> — Smart presets that automatically set the drop point and final duration.</li>
                  <li style={{ marginBottom: '6px' }}>📍 <strong>SET IN</strong> — Manually set the exact entry point of the mix (Red marker).</li>
                  <li style={{ marginBottom: '6px' }}>🎯 <strong>SET OUT</strong> — Manually set the exact end point of the mix (Blue marker).</li>
                </ul>
                
                <h4 style={{ color: '#fff', fontSize: '14px', margin: '15px 0 5px 0' }}>Transition Effects</h4>
                <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0, fontSize: '13px', lineHeight: '1.5', color: '#aaa' }}>
                  <li style={{ marginBottom: '6px' }}>📈 <strong>POWER STOP</strong> — Classic vinyl brake effect.</li>
                  <li style={{ marginBottom: '6px' }}>💥 <strong>BEAT DROP</strong> — Bass boost + high impact transition.</li>
                  <li style={{ marginBottom: '6px' }}>📻 <strong>RADIO EQ</strong> — High-pass filter transition (radio/phone effect).</li>
                </ul>
                
                <div style={{ borderBottom: '1px solid #27272a', margin: '15px 0' }}></div>
                <p style={{ fontSize: '11px', color: '#888', margin: 0 }}>
                  🔒 <strong>Privacy Note:</strong> Your privacy is important, your data is safe under your profile. Free accounts can preview unlimited mixes. Exporting requires an active plan or trial.
                </p>
              </>
            )}

            <button 
              onClick={closeGuideModal}
              style={{ width: '100%', padding: '12px', marginTop: '20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(45deg, #a370f7, #00f0ff)', color: '#fff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 12px rgba(163,112,247,0.3)', textTransform: 'uppercase' }}
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {/* 🚀 IL PONTE DI LANCIO DELLA CONDIVISIONE */}
      {readyToShare && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 9999999, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(8px)' }}>
          <div style={{ backgroundColor: '#111', border: `2px solid ${readyToShare.platform === 'tiktok' ? '#00f2fe' : '#fe0979'}`, borderRadius: '15px', padding: '40px 30px', width: '90%', maxWidth: '400px', textAlign: 'center', boxShadow: `0 0 50px ${readyToShare.platform === 'tiktok' ? 'rgba(0, 242, 254, 0.2)' : 'rgba(254, 9, 121, 0.2)'}` }}>
            <h2 style={{ color: '#fff', fontSize: '24px', marginBottom: '15px' }}>Video Ready! 🎬</h2>
            <p style={{ color: '#ccc', fontSize: '15px', lineHeight: '1.5', marginBottom: '30px' }}>Your Magic Mix has been successfully generated and is ready to go viral.</p>
            <button onClick={executeShare} style={{ width: '100%', padding: '15px', borderRadius: '10px', border: 'none', background: readyToShare.platform === 'tiktok' ? 'linear-gradient(45deg, #00f2fe, #4facfe)' : 'linear-gradient(45deg, #f5576c, #f093fb)', color: '#fff', fontSize: '18px', fontWeight: '900', cursor: 'pointer', marginBottom: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.5)' }}>🚀 Share to {readyToShare.platform === 'tiktok' ? 'TikTok' : 'Instagram'}</button>
          </div>
        </div>
      )}

      {/* 🛑 DYNAMIC MODAL PAYWALL */}
      {showPaywall && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 9999999, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(8px)' }}>
          <div style={{ backgroundColor: '#111', border: '2px solid #ff4444', borderRadius: '15px', padding: '40px 30px', width: '90%', maxWidth: '400px', textAlign: 'center', boxShadow: '0 0 50px rgba(255, 68, 68, 0.2)' }}>
            {user?.status === 'Pro' ? (
              <>
                <h2 style={{ color: '#fff', fontSize: '24px', marginBottom: '15px' }}>Monthly exports exhausted! 🚫</h2>
                <p style={{ color: '#aaa', marginBottom: '20px' }}>Redirecting to secure checkout...</p>
                <button 
                  onClick={async (e) => { 
                    const btn = e.target; const originalText = btn.innerText; btn.innerText = "Opening checkout..."; btn.style.opacity = "0.7"; btn.style.cursor = "wait";
                    try {
                      const response = await fetch(`${API}/create-checkout-session`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ price_id: 'price_1TVVpc2MfeHS1EKL4kIfPEYU', user_id: user?.user_id }) });
                      const data = await response.json(); if (data.checkout_url) { window.location.href = data.checkout_url; } else { alert("Error loading checkout. Check network."); btn.innerText = originalText; btn.style.opacity = "1"; btn.style.cursor = "pointer"; }
                    } catch (error) { alert("Stripe connection error. Please try again."); btn.innerText = originalText; btn.style.opacity = "1"; btn.style.cursor = "pointer"; }
                  }} 
                  style={{ width: '100%', padding: '15px', borderRadius: '10px', border: 'none', background: 'linear-gradient(45deg, #00f2fe, #4facfe)', color: '#fff', fontSize: '18px', fontWeight: '900', cursor: 'pointer', marginBottom: '15px', boxShadow: '0 5px 15px rgba(0, 242, 254, 0.4)' }}>
                  💎 Get 10 Exports - €2.00
                </button>
              </>
            ) : (
              <>
                <h2 style={{ color: '#fff', fontSize: '24px', marginBottom: '15px' }}>Unlock Full Export! 🚀</h2>
                <p style={{ color: '#ccc', fontSize: '15px', lineHeight: '1.5', marginBottom: '30px' }}>Export is reserved for PRO users. Get 20 high-quality mixes per month and unlimited features.</p>
                <button 
                  onClick={async (e) => { 
                    const btn = e.target; btn.innerText = "Opening checkout..."; btn.style.opacity = "0.7"; btn.style.cursor = "wait";
                    try {
                      const response = await fetch(`${API}/create-checkout-session`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ price_id: 'price_1TVVna2MfeHS1EKLOIC3bJEF', user_id: user?.user_id }) });
                      const data = await response.json(); if (data.checkout_url) { window.location.href = data.checkout_url; } else { alert("Error loading checkout. Check network."); btn.innerText = "🔥 Go PRO - €4.00/mo"; btn.style.opacity = "1"; btn.style.cursor = "pointer"; }
                    } catch (error) { alert("Stripe connection error. Please try again."); btn.innerText = "🔥 Go PRO - €4.00/mo"; btn.style.opacity = "1"; btn.style.cursor = "pointer"; }
                  }} 
                  style={{ width: '100%', padding: '15px', borderRadius: '10px', border: 'none', background: 'linear-gradient(45deg, #00f2fe, #4facfe)', color: '#fff', fontSize: '18px', fontWeight: '900', cursor: 'pointer', marginBottom: '15px', boxShadow: '0 5px 15px rgba(0, 242, 254, 0.4)' }}>
                  🔥 Go PRO - €4.00/mo
                </button>
              </>
            )}
            <button onClick={() => setShowPaywall(false)} style={{ background: 'transparent', border: 'none', color: '#888', textDecoration: 'underline', cursor: 'pointer' }}>Close and return to mixing</button>
          </div>
        </div>
      )}

      {/* MODAL OVERLAY DI LOGIN - STRADA B */}
      {showAuthModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 2147483647, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(5px)' }}>
          <div style={{ backgroundColor: '#111', border: '1px solid #00f0ff', borderRadius: '15px', padding: '30px', width: '90%', maxWidth: '420px', position: 'relative', boxShadow: '0 20px 50px rgba(0,240,255,0.1)' }}>
            <button onPointerDown={() => setShowAuthModal(false)} style={{ position: 'absolute', top: '15px', right: '15px', background: 'transparent', border: 'none', color: '#888', fontSize: '20px', cursor: 'pointer' }}>✖</button>
            <h2 style={{ color: '#fff', marginTop: 0, marginBottom: '25px', fontSize: '20px', textAlign: 'center' }}>🚀 Download Your Mix</h2>
            <LoginButton user={null} mode="form" customTitle="Your mix is ready. Sign in to download it. (PRO - €4.00/mo)" />
          </div>
        </div>
      )}
      <Toaster theme="dark" position="top-center" toastOptions={{ style: { background: 'var(--obsidian)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)' } }} />
    </div>
  );
};

export default Home;